import { createChatRoom, getSignedUrlForGet, updateTask } from '@commenda/api';
import { Icons, showNotification } from '@commenda/kakashi';
import { TWorkflowExecution } from '@commenda/types';
import { MonthWithMonthValue } from '@commenda/types/src/compliance-calendar/compliances/types';
import {
  ServiceRequestDetails,
  TaskGroupWithSLA,
} from '@commenda/types/src/service-request/types';
import {
  getRelativeDateDisplay,
  NonTimezoneSensitiveDate,
  ServiceRequestModel,
} from '@commenda/utils';
import { transformToTitleCase } from '@commenda/utils/transformString';
import { TSrPageTask } from '@commenda/types/src/service-request/types';
import { ChatRoomType, LegacyDocumentType, ServiceRequestStatus, TaskParty, TaskStatus } from '@prisma/client';
import { mutate as globalMutate, KeyedMutator } from 'swr';
import { ServiceRequestTaskGroupView } from './types';

// This will mark the task passed as COMPLETED and set other statuses
export const updateTaskStatus = async (params: {
  task: TSrPageTask;
  status: TaskStatus;
  mutate: KeyedMutator<unknown>;
  metadata?: Record<string, any>;
}) => {
  const { task, mutate, status, metadata } = params;
  const res = await updateTask({
    id: task.id,
    data: { status, metadata },
  });
  if (!res.ok) {
    return false;
  }
  await mutate();
  return true;
};

const FILE_OPEN_FAILED_NOTIFICATION = {
  valid: false,
  title: 'Unable to open file',
  message: 'We could not generate a link for this file. Please try again.',
} as const;

// The tab is opened before the network round-trip so popup blockers (which only
// allow window.open inside a user gesture) do not block it.
export const viewUploadedFile = async (fileId: number, openNewTab = true) => {
  const tab = openNewTab ? window.open('', '_blank') : null;

  const data = await getSignedUrlForGet(fileId);
  const signedUrl = data?.signedUrl;

  if (!signedUrl) {
    tab?.close();
    showNotification(FILE_OPEN_FAILED_NOTIFICATION);
    return false;
  }

  if (!openNewTab) {
    window.location.href = signedUrl;
  } else if (tab) {
    tab.location.href = signedUrl;
  } else {
    window.open(signedUrl, '_blank');
  }

  return true;
};

export const viewUploadedFiles = async (fileIds: number[]) => {
  const tabs = fileIds.map(() => window.open('', '_blank'));
  const results = await Promise.all(fileIds.map((fileId) => getSignedUrlForGet(fileId)));

  let failed = 0;
  results.forEach((data, index) => {
    const tab = tabs[index];
    const signedUrl = data?.signedUrl;
    if (!signedUrl) {
      tab?.close();
      failed += 1;
      return;
    }
    if (tab) tab.location.href = signedUrl;
    else window.open(signedUrl, '_blank');
  });

  if (failed > 0) {
    showNotification({
      ...FILE_OPEN_FAILED_NOTIFICATION,
      title: failed === fileIds.length ? 'Unable to open files' : `Unable to open ${failed} of ${fileIds.length} files`,
    });
  }

  return failed === 0;
};

export const isDocumentPresent = ({
  serviceRequest,
  documentType,
}: {
  serviceRequest: ServiceRequestDetails;
  documentType: LegacyDocumentType;
}) => {
  return serviceRequest?.corporation?.corporationDocuments?.some(
    (each) => each?.documentType === documentType
  );
};

export const isDocumentPresentForAnyStakeholder = ({
  corporationStakeholders,
  documentType,
}: {
  corporationStakeholders: ServiceRequestDetails['corporation']['corporationHasDirector'];
  documentType: LegacyDocumentType;
}) => {
  if (!corporationStakeholders || !Array.isArray(corporationStakeholders)) {
    return false;
  }

  return corporationStakeholders.every((stakeholder) =>
    stakeholder?.documents?.some((doc) => doc?.documentType === documentType)
  );
};

export const areDocumentsPresent = ({
  serviceRequest,
  documentTypes,
}: {
  serviceRequest: ServiceRequestDetails;
  documentTypes: LegacyDocumentType[];
}) => {
  return documentTypes.every((documentType) =>
    serviceRequest?.corporation?.corporationDocuments?.some(
      (each) => each?.documentType === documentType
    )
  );
};

export const finStatementAlertDesc = ({
  fiscalYearDayStart,
  fiscalYearMonthStart,
}: {
  fiscalYearDayStart: number;
  fiscalYearMonthStart: number;
}) => {
  const currentYear = new Date().getFullYear();

  if (!fiscalYearDayStart || !fiscalYearMonthStart) {
    return '';
  }
  const { fiscalYearEndDate, fiscalYearEndMonth } =
    NonTimezoneSensitiveDate.convertFiscalYearStartToEnd(
      fiscalYearDayStart,
      fiscalYearMonthStart
    );

  const monthName = transformToTitleCase(
    MonthWithMonthValue[fiscalYearEndMonth]
  );

  const fiscalYearEndMethod = ` ${monthName} ${fiscalYearEndDate}, ${currentYear}`;

  return `Ensure that these statements are as of ${fiscalYearEndMethod}.`;
};

export const getTaskStatus = (
  task: TSrPageTask,
  serviceRequest: ServiceRequestDetails
) => {
  if (serviceRequest.status === ServiceRequestStatus.ARCHIVED) {
    return TaskStatus.BACKLOG;
  }
  return task.status;
};

export const isSkippedByBranch = (
  task: Pick<TSrPageTask, 'metadata'>
): boolean => task.metadata?.skippedByBranch === true;

// Whether this task is the step the flow is currently waiting on. Workflow
// steps are gated by the engine (unreached steps stay BACKLOG, parallel
// branches are actionable together); for other tasks it is the first
// non-completed visible task in its group. Later steps render their labels but
// no actions.
export const isCurrentTimelineTask = (
  task: TSrPageTask & { taskGroup?: { tasks: TSrPageTask[] } }
): boolean => {
  if (task.workflowDefinitionStepId) return true;
  const firstIncomplete = (task.taskGroup?.tasks ?? [])
    .filter((t) => !isSkippedByBranch(t))
    .find((t) => t.status !== TaskStatus.COMPLETED);
  return !firstIncomplete || firstIncomplete.id === task.id;
};

export const invalidateActivityLogs = (serviceRequestId: string) => {
  globalMutate((key) => {
    return (
      typeof key === 'string' &&
      key.startsWith('service-request/logs') &&
      key.includes(`serviceRequestId=${serviceRequestId}`)
    );
  });
};

export async function handleChatRoomCreation(
  serviceRequest: ServiceRequestDetails,
  mutate: KeyedMutator<unknown>
) {
  if (!serviceRequest || !mutate) return;

  const res = await createChatRoom({
    createChatRoomProps: {
      chatRoomType: ChatRoomType.SERVICE_REQUEST_THREAD,
      serviceRequestId: serviceRequest?.id,
      complianceId: serviceRequest?.complianceItemId,
    },
  });

  if (!res.ok) {
    console.error('Failed to create chat room', res);
    return;
  }

  mutate();
}

export const getStatusIcon = (
  taskGroup: TaskGroupWithSLA,
  allTaskGroups: TaskGroupWithSLA[]
) => {
  if (!taskGroup) return null;

  const orderNumber = taskGroup.legacyServiceRequestTaskGroupOrderNumber ?? 0;

  if (taskGroup.status === ServiceRequestStatus.COMPLETED) {
    return (
      <div className="size-8 rounded-full bg-utility-success-500 flex items-center justify-center shrink-0">
        <Icons.Check className="size-4 text-white" />
      </div>
    );
  }

  const currentTaskGroups =
    ServiceRequestModel.getCurrentTaskGroups(allTaskGroups);
  const isCurrentTaskGroup = currentTaskGroups?.find(
    (group) => group.id === taskGroup.id
  );

  if (isCurrentTaskGroup) {
    return (
      <div className="size-8 rounded-full bg-bg-brand-solid border border-fg-brand flex items-center justify-center shrink-0">
        <span className="text-sm font-semibold text-white">{orderNumber}</span>
      </div>
    );
  }

  return (
    <div className="size-8 rounded-full bg-bg-primary border border-border-secondary flex items-center justify-center shrink-0">
      <span className="txt-small-medium text-text-placeholder">
        {orderNumber}
      </span>
    </div>
  );
};

export const isTaskPendingOnRole = (
  pendingOn: TaskParty | null,
  role: TaskParty
) =>
  role === TaskParty.ADMIN
    ? pendingOn === TaskParty.ADMIN || pendingOn === TaskParty.AGENT
    : pendingOn === role;

export const getButtonOptionsByRole = (
  role: TaskParty
): { label: string; value: ServiceRequestTaskGroupView }[] =>
  role === TaskParty.AGENT || role === TaskParty.ADMIN
    ? [
      { label: 'Timeline', value: ServiceRequestTaskGroupView.TIMELINE },
      { label: 'Tasks', value: ServiceRequestTaskGroupView.TASKS },
    ]
    : [
      { label: 'Tasks', value: ServiceRequestTaskGroupView.TASKS },
      { label: 'Timeline', value: ServiceRequestTaskGroupView.TIMELINE },
    ];

export function ComplianceTaskDueDate({ dueDate }: { dueDate: string }) {
  if (!dueDate) return null;

  const relativeDate = getRelativeDateDisplay(dueDate);

  const dateDisplay =
    relativeDate === 'Today'
      ? 'Due today'
      : relativeDate === 'Tomorrow'
        ? 'Due tomorrow'
        : `Due ${relativeDate}`;

  if (relativeDate === 'Today') {
    return (
      <div className="flex items-center gap-xs">
        <span className="text-text-error txt-extra-small-regular">
          {dateDisplay}
        </span>
        <Icons.AlertTriangle className="size-3 text-text-error" />
      </div>
    );
  }

  return <span className="txt-extra-small-regular">{dateDisplay}</span>;
}

// requestData on workRequest are arbitrary keys, so we need to make them human readable
// See ServiceRequestServiceInformationTab.tsx for usage
export const humanizeKey = (key: string) => {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d+)/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase());
};

export const SericeRequestUnknownTask = {
  taskId: 'UNKNOWN_TASK',
  title: 'Unknown Task Title. Contact Commenda.',
};

export const SericeRequestUnknownTaskGroup = {
  taskGroupId: 'UNKNOWN_TASK_GROUP',
  title: 'Unknown Task Group Title. Contact Commenda.',
};

export const shouldRenderWorkflowStepTask = (
  task: TSrPageTask,
  serviceRequest: ServiceRequestDetails
): boolean => {
  return !!(
    task.workflowDefinitionStepId && serviceRequest.workflowExecutionId
  );
};


export const getStepGroupDescription = (
  taskGroup: { workflowDefinitionStepGroupId?: string | null },
  workflowExecution?: TWorkflowExecution
): string | undefined => {
  if (!taskGroup.workflowDefinitionStepGroupId) return undefined;
  const stepGroups =
    workflowExecution?.WorkflowTemplateVersion?.definition?.stepGroups;
  if (!stepGroups) return undefined;
  return stepGroups.find(
    (g) => g.id === taskGroup.workflowDefinitionStepGroupId
  )?.description;
};
